import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Popconfirm } from "antd";
import { FaEdit, FaRegStar, FaStar, FaTag } from "react-icons/fa";
import { DeleteOutlined } from "@ant-design/icons";

import { IFonteSaldoDeContas } from "../../../../../interfaces";
import { IOrdenacaoFonteItem, IUpdateCategoriaFonte, IUpdateDescricaoFonte } from "../../interfaces";
import { stateGlobal } from "../../../../../../../../interfaces/Global";

import FonteFormModal from "../FonteFormModal";

export interface IFonteActionsProps {
    item: IOrdenacaoFonteItem;
    categoriaId: number | null;
    onChangeDescricao: (item: IOrdenacaoFonteItem) => void;
    onOnlyCategoriaUpdated: (fonte: { numero: string, categoriaId: number }) => void;
    onUpdateCategoriaFonte: (data: IUpdateCategoriaFonte) => Promise<void>;
    onUpdateDescricaoFonte: (data: IUpdateDescricaoFonte) => Promise<IFonteSaldoDeContas | null>;
    onDeleteFonte?: (fonteId: number) => Promise<void>;
    onToggleFavorite: () => void;
    favorita: boolean;
    solicitacaoId: number;
    hideFavorite?: boolean;
    isOrfaTab?: boolean;
}

export default function FonteActions({
    item,
    categoriaId,
    onChangeDescricao,
    onOnlyCategoriaUpdated,
    onToggleFavorite,
    favorita,
    solicitacaoId,
    onUpdateCategoriaFonte,
    onUpdateDescricaoFonte,
    onDeleteFonte,
    hideFavorite,
    isOrfaTab,
}: IFonteActionsProps) {
    const [isEditDescricaoModalVisible, setIsEditDescricaoModalVisible] = useState(false);
    const [isEditCategoriaModalVisible, setIsEditCategoriaModalVisible] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const propsState = useSelector((state: stateGlobal) => state.global);

    const isGroup = useMemo(() => {
        const fontes = item.numero.split(',');

        return fontes.length > 1;
    }, [item.numero]);

    const handleClickEditDescricao = () => {
        setIsEditDescricaoModalVisible(true);
    }

    const handleClickEditCategoria = () => {
        setIsEditCategoriaModalVisible(true);
    }

    const handleCloseEditModal = () => {
        setIsEditDescricaoModalVisible(false);
        setIsEditCategoriaModalVisible(false);
    }

    const handleFonteUpdate = (fonte: IFonteSaldoDeContas) => {
        onChangeDescricao({ descricao: fonte.descricao, favorita: fonte.favorita, numero: fonte.numero, ordem: item.ordem, categoriaId: fonte.categoriaId });
        handleCloseEditModal();
    }

    const handleOnlyCategoriaUpdated = (fonte: { numero: string; categoriaId: number; }) => {
        onOnlyCategoriaUpdated(fonte);
        handleCloseEditModal();
    }

    const handleDelete = async () => {
        if (!item.id || !onDeleteFonte) {
            return;
        }

        try {
            setIsDeleting(true);
            await onDeleteFonte(item.id);
        } catch (error) {
            // Handled by caller / EmitirErro
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                }}
            >
                <div
                    onClick={handleClickEditDescricao}
                    style={{
                        cursor: 'pointer'
                    }}
                >
                    <FaEdit
                        size={16}
                        color={propsState.theme === 'light' ? "#0000006f" : '#ffffff73'}
                    />
                </div>

                <div
                    onClick={handleClickEditCategoria}
                    style={{
                        cursor: 'pointer'
                    }}
                >
                    <FaTag
                        size={14}
                        color={propsState.theme === 'light' ? "#0000006f" : '#ffffff73'}
                    />
                </div>

                {!hideFavorite && (
                    <div
                        onClick={onToggleFavorite}
                        style={{
                            cursor: 'pointer'
                        }}
                    >
                        {
                            favorita ? (
                                <FaStar
                                    size={16}
                                    color="#fe9a00"
                                />
                            ) : (
                                <FaRegStar
                                    size={16}
                                    color={propsState.theme === 'light' ? "#0000006f" : '#ffffff73'}
                                />
                            )
                        }
                    </div>
                )}

                {(Boolean(item.orfa) || isOrfaTab) && item.id != null && onDeleteFonte && (
                    <Popconfirm
                        title={
                            <div style={{ maxWidth: 260 }}>
                                <div style={{ fontWeight: 600, marginBottom: 4 }}>Deseja deletar esta fonte?</div>
                                <div style={{ fontSize: 12 }}>
                                    Esta ação apagará o histórico de empenhos e restos desta fonte e não poderá ser desfeita.
                                </div>
                            </div>
                        }
                        onConfirm={handleDelete}
                        okText="Sim, deletar"
                        cancelText="Cancelar"
                        okButtonProps={{ danger: true, loading: isDeleting }}
                        disabled={isDeleting}
                        placement="topRight"
                        style={{
                            padding: 0
                        }}
                    >
                        <div
                            style={{
                                cursor: isDeleting ? 'not-allowed' : 'pointer',
                            }}
                        >
                            <DeleteOutlined
                                style={{
                                    fontSize: 14,
                                    color: '#ff4d4f',
                                    verticalAlign: 0
                                }}
                            />
                        </div>
                    </Popconfirm>
                )}
            </div>

            <FonteFormModal
                visible={isEditDescricaoModalVisible || isEditCategoriaModalVisible}
                title={isEditDescricaoModalVisible ? `Editar ${isGroup ? 'nome do grupo' : 'descrição da fonte'}` : `Editar categoria ${isGroup ? 'do grupo' : 'da fonte'}`}
                onClose={handleCloseEditModal}
                fonteFormProps={{
                    onFinish: handleFonteUpdate,
                    solicitacaoId: solicitacaoId,
                    fonte: item,
                    initialCategoriaId: categoriaId,
                    onlyDescricao: isEditDescricaoModalVisible,
                    onlyCategoria: isEditCategoriaModalVisible,
                    onOnlyCategoriaUpdated: handleOnlyCategoriaUpdated,
                    onUpdateCategoriaFonte,
                    onUpdateDescricaoFonte,
                }}
            />
        </>
    )
}
